import { CImage } from '@coreui/react-pro'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import pccIcon from '../../../assets/icons/PCCIcon.svg'
import { toast } from 'react-toastify'
import { CSpinner } from '@coreui/react-pro'
import {
  getPCCAllergies,
  getPCCConditions,
  getPCCFunctionalStatus,
  getPCCImmunizations,
  getPCCMedications,
  getPCCObservations,
} from '../../../helper/pccApiHelper'

function formatMedicationText(results) {
  let medications = ''
  results?.forEach((result) => {
    let description = result.description === 'scrubbed' ? result.generic : result.description
    let directions = result.directions
    if (result.directions === 'scrubbed') {
      // scrubbed means Medication no longer being used
      return
    }

    medications += `${description}, ${directions}\n`
  })
  medications = medications.replaceAll('&lt;', '<').replaceAll('&gt;', '>').replaceAll('&amp;', '&')
  return medications
}

export default function PointClickCareDataCopy({ patient }) {
  const settings = useSelector((state) => state.user?.settings)
  const serviceDateValue = Boolean(settings.pccDosFilter) ? patient?.service_date_from : null

  const [isLoadingType, setIsLoadingType] = useState()

  const pullPCCData = async () => {
    toast.dismiss()
    toast.info('Retrieving Patient Data...')

    let patientId = patient?.patient_id
    let pcc = patient?.pointclickcare?.orgUuid

    let text = ''

    if (pcc && patientId) {
      let medications = ''
      await getPCCMedications(patientId, serviceDateValue).then((results) => {
        if (results && results.length > 0) {
          medications = formatMedicationText(results)
        }
      })
      if (text !== '') {
        text += '\n'
      }
      if (medications !== '') {
        text += 'MEDICATIONS:\n' + medications
      } else {
        text += 'MEDICATIONS: No known medications'
      }
      if (text !== '') {
        text += '\n'
      }

      let allergies = ''
      await getPCCAllergies(patientId, serviceDateValue).then((results) => {
        if (results && results.length > 0) {
          results.forEach((result) => {
            let allergy = result?.allergen
            let onsetDate = result.onsetDate ? ' (' + result.onsetDate + ')' : ''
            if (allergy !== undefined && allergy !== '') {
              if (allergies !== '') {
                allergies += '\n'
              }
              allergies += allergy + onsetDate
            }
          })
        }
      })
      if (text !== '') {
        text += '\n'
      }
      if (allergies !== '') {
        text += 'ALLERGIES:\n' + allergies
      } else {
        text += 'ALLERGIES: No known allergies'
      }
      if (text !== '') {
        text += '\n'
      }

      let immunizations = ''
      await getPCCImmunizations(patientId, serviceDateValue).then((results) => {
        if (results && results.length > 0) {
          results.forEach((result) => {
            if (immunizations !== '') {
              immunizations += '\n'
            }
            immunizations +=
              result.immunization +
              ' (' +
              result.consentStatus +
              ' - ' +
              result.consentConfirmedDate +
              ')'
          })
        }
      })
      if (text !== '') {
        text += '\n'
      }
      if (immunizations !== '') {
        text += 'IMMUNIZATIONS:\n' + immunizations
      } else {
        text += 'IMMUNIZATIONS: No known immunizations'
      }
      if (text !== '') {
        text += '\n'
      }

      let vitals = ''
      await getPCCObservations(patientId, serviceDateValue).then((results) => {
        if (results && results.length > 0) {
          results.forEach((result) => {
            if (result.type === 'bloodPressure') {
              if (vitals !== '') {
                vitals += '\n'
              }
              vitals =
                vitals +
                (result.type.toUpperCase() +
                  (result.method ? ' (' + result.method + ')' : '') +
                  ': ' +
                  result.systolicValue +
                  '/' +
                  result.diastolicValue +
                  ' ' +
                  (result.unit ? result.unit : ''))
            }
            if (result.value !== undefined) {
              if (vitals !== '') {
                vitals += '\n'
              }
              vitals +=
                result.type.toUpperCase() +
                (result.method ? '(' + result.method + ')' : '') +
                ': ' +
                result.value +
                ' ' +
                (result.unit ? result.unit : '')
            }
          })
        }
      })
      if (text !== '') {
        text += '\n'
      }
      if (vitals !== '') {
        text += 'VITALS:\n' + vitals
      } else {
        text += 'VITALS: No recorded vitals'
      }
      if (text !== '') {
        text += '\n'
      }

      let pmhx = ''
      await getPCCConditions(patientId, serviceDateValue).then((results) => {
        if (results && results.length > 0) {
          results.forEach((result) => {
            if (pmhx !== '') {
              pmhx += '\n'
            }
            if (result?.icd10Description) {
              pmhx = pmhx + result.icd10Description
            }
          })
        }
      })
      if (text !== '') {
        text += '\n'
      }
      if (pmhx !== '') {
        text += 'PMHX:\n' + pmhx
      } else {
        text += 'PMHX: No recorded pmhx'
      }
      if (text !== '') {
        text += '\n'
      }
    }

    let functionalStatus = ''
    await getPCCFunctionalStatus(patientId, serviceDateValue).then((results) => {
      if (results && results.length > 0) {
        results.forEach((result) => {
          if (functionalStatus !== '') {
            functionalStatus += '\n'
          }
          functionalStatus =
            functionalStatus + result?.questionDescription + ': ' + result?.response?.description
        })
      }
    })
    if (text !== '') {
      text += '\n'
    }
    if (functionalStatus !== '') {
      text += 'NURSING FUNCTIONAL STATUS:\n' + functionalStatus
    } else {
      text += 'NURSING FUNCTIONAL STATUS: No ADL report on file.'
    }
    if (text !== '') {
      text += '\n'
    }

    navigator.clipboard.writeText(text)
    setTimeout(() => {
      toast.dismiss()
      toast.success('Patient data copied to clipboard - use CTRL-V to paste where appropriate.')
    }, 1000)
  }

  const pullConditions = async () => {
    let patientId = patient?.patient_id
    let pcc = patient?.pointclickcare?.orgUuid
    let text = 'PMHX:'

    if (pcc && patientId) {
      let pmhx = ''
      await getPCCConditions(patientId, serviceDateValue).then((results) => {
        if (results && results.length > 0) {
          results.forEach((result) => {
            if (pmhx !== '') {
              pmhx = pmhx + '\n'
            }
            if (result?.icd10Description) {
              pmhx = pmhx + result.icd10Description
            }
          })
        }
      })

      if (pmhx !== '') {
        text = text + '\n' + pmhx
      } else {
        text = text + 'No recorded pmhx'
      }
      if (text !== '') {
        text = text + '\n'
      }
    }

    navigator.clipboard.writeText(text)
    toast.dismiss()
    toast.success('Conditions copied to clipboard - use CTRL-V to paste where appropriate.')
  }

  const pullObservations = async () => {
    let patientId = patient?.patient_id
    let pcc = patient?.pointclickcare?.orgUuid

    let text = 'VITALS:'

    if (pcc && patientId) {
      let vitals = ''
      await getPCCObservations(patientId, serviceDateValue).then((results) => {
        if (results && results.length > 0) {
          results.forEach((result) => {
            if (result.type === 'bloodPressure') {
              if (vitals !== '') {
                vitals = vitals + '\n'
              }
              vitals =
                vitals +
                (result.type.toUpperCase() +
                  (result.method ? '(' + result.method + ')' : '') +
                  ': ' +
                  result.systolicValue +
                  '/' +
                  result.diastolicValue +
                  ' ' +
                  (result.unit ? result.unit : ''))
            }
            if (result.value !== undefined) {
              if (vitals !== '') {
                vitals = vitals + '\n'
              }
              vitals =
                vitals +
                (result.type.toUpperCase() +
                  (result.method ? ' (' + result.method + ')' : '') +
                  ': ' +
                  result.value +
                  ' ' +
                  (result.unit ? result.unit : ''))
            }
          })
        }
      })

      if (vitals !== '') {
        text = text + '\n' + vitals
      } else {
        text = text + 'No recorded vitals'
      }
      if (text !== '') {
        text = text + '\n'
      }
    }
    navigator.clipboard.writeText(text)
    toast.dismiss()
    toast.success('Observation data copied to clipboard - use CTRL-V to paste where appropriate.')
  }

  const pullMedications = async () => {
    let patientId = patient?.patient_id
    let pcc = patient?.pointclickcare?.orgUuid

    let text = 'MEDICATIONS:'

    if (pcc && patientId) {
      let medications = ''
      await getPCCMedications(patientId, serviceDateValue).then((results) => {
        if (results && results.length > 0) {
          medications = formatMedicationText(results)
        }
      })

      if (medications !== '') {
        text = text + '\n' + medications
      } else {
        text = text + 'No known medications'
      }
      if (text !== '') {
        text = text + '\n'
      }
    }

    navigator.clipboard.writeText(text)
    toast.dismiss()
    toast.success('Medication data copied to clipboard - use CTRL-V to paste where appropriate.')
  }

  const pullAllergies = async () => {
    let patientId = patient?.patient_id
    let pcc = patient?.pointclickcare?.orgUuid

    let text = 'ALLERGIES:'

    if (pcc && patientId) {
      let allergies = ''
      await getPCCAllergies(patientId, serviceDateValue).then((results) => {
        if (results && results.length > 0) {
          results.forEach((result) => {
            let allergy = result?.allergen
            let onsetDate = result.onsetDate ? ' (' + result.onsetDate + ')' : ''
            if (allergy !== undefined && allergy !== '') {
              if (allergies !== '') {
                allergies = allergies + '\n'
              }
              allergies = allergies + allergy + onsetDate
            }
          })
        }
      })

      if (allergies !== '') {
        text = text + '\n' + allergies
      } else {
        text = text + 'No known allergies'
      }
      if (text !== '') {
        text = text + '\n'
      }
    }

    navigator.clipboard.writeText(text)
    toast.dismiss()
    toast.success('Allergy data copied to clipboard - use CTRL-V to paste where appropriate.')
  }

  const pullImmunizations = async () => {
    let patientId = patient?.patient_id
    let pcc = patient?.pointclickcare?.orgUuid

    let text = 'IMMUNIZATIONS:'

    if (pcc && patientId) {
      let immunizations = ''
      await getPCCImmunizations(patientId, serviceDateValue).then((results) => {
        if (results && results.length > 0) {
          results.forEach((result) => {
            if (immunizations !== '') {
              immunizations = immunizations + '\n'
            }
            immunizations =
              immunizations +
              result.immunization +
              ' (' +
              result.consentStatus +
              ' - ' +
              result.consentConfirmedDate +
              ')'
          })
        }
      })

      if (immunizations !== '') {
        text = text + '\n' + immunizations
      } else {
        text = text + 'No known immunizations'
      }
      if (text !== '') {
        text = text + '\n'
      }
    }

    navigator.clipboard.writeText(text)
    toast.dismiss()
    toast.success('Immunization data copied to clipboard - use CTRL-V to paste where appropriate.')
  }

  const pullFunctionalStatus = async () => {
    let patientId = patient?.patient_id
    let pcc = patient?.pointclickcare?.orgUuid

    let text = 'NURSING FUNCTIONAL STATUS:'

    if (pcc && patientId) {
      let functionalStatus = ''
      await getPCCFunctionalStatus(patientId, serviceDateValue).then((results) => {
        if (results && results.length > 0) {
          results.forEach((result) => {
            if (functionalStatus !== '') {
              functionalStatus = functionalStatus + '\n'
            }
            functionalStatus =
              functionalStatus + result?.questionDescription + ': ' + result?.response?.description
          })
        }
      })

      if (functionalStatus !== '') {
        text = text + '\n' + functionalStatus
      } else {
        text = text + 'No ADL report on file.'
      }
      if (text !== '') {
        text = text + '\n'
      }
    }

    navigator.clipboard.writeText(text)
    toast.dismiss()
    toast.success(
      'Nursing Functional Status data copied to clipboard - use CTRL-V to paste where appropriate.',
    )
  }

  const icons = [
    {
      type: 'All Clinical Data',
      className: 'fa-solid fa-database',
      onClick: pullPCCData,
    },
    {
      type: 'Immunizations',
      className: 'fa-solid fa-syringe',
      onClick: pullImmunizations,
    },
    {
      type: 'Allergies',
      className: 'fas fa-hand-dots',
      onClick: pullAllergies,
    },
    {
      type: 'Meds',
      className: 'fa-solid fa-pills',
      onClick: pullMedications,
    },
    {
      type: 'Vitals',
      className: 'fas fa-eye',
      onClick: pullObservations,
    },
    {
      type: 'PMHX',
      className: 'fa-solid fa-person-dots-from-line',
      onClick: pullConditions,
    },
    {
      type: 'Nursing Functional Status',
      className: 'fa-solid fa-walker',
      onClick: pullFunctionalStatus,
    },
  ]

  return (
    <div className="pcc-clipboard">
      <CImage className="mb-1" src={pccIcon} title="Copy to Clipboard" />
      <div className="title">Copy to Clipboard:</div>
      <div className={'icon-row'}>
        {icons.map((obj) => {
          return (
            <LabelIcon
              key={obj.type + '-icon'}
              {...obj}
              onClick={() => {
                setIsLoadingType(obj.type)
                obj.onClick().finally(() => {
                  setIsLoadingType(null)
                })
              }}
              isLoading={isLoadingType === obj.type}
            />
          )
        })}
      </div>
    </div>
  )
}

function LabelIcon({ type, className, onClick, isLoading }) {
  const [showLabel, setShowLabel] = useState(false)

  return (
    <div onMouseLeave={() => setShowLabel(false)} onClick={onClick}>
      <div onMouseEnter={() => setShowLabel(true)}>
        {isLoading ? (
          <CSpinner
            style={{ '--cui-spinner-width': '1.05rem', '--cui-spinner-height': '1.05rem' }}
          />
        ) : (
          <i className={className}></i>
        )}
      </div>
      {showLabel && <span>{type}</span>}
    </div>
  )
}
